import React, { Suspense, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { TOOLS } from '@/shared/config/tools';
import { ToolErrorBoundary } from '@/components/shared/ToolErrorBoundary';
import { ToolFeedback } from '@/components/shared/ToolFeedback';
import { ToolGuide } from '@/components/ToolGuide';
import type { LandingDetails } from '@/components/ToolGuide';
import { recordToolUse } from '@/shared/lib/tool-usage';

interface ToolPageProps {
  toolId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  pageTitle?: string;
  pageDescription?: string;
  landing?: LandingDetails;
  componentProps?: Record<string, unknown>;
}

const TOOL_WORKSPACE_HEIGHTS: Record<string, string> = {
  'image-converter': 'min-h-[640px] lg:min-h-[480px]',
  'mock-data': 'min-h-[1846px] md:min-h-[1874px] lg:min-h-[1280px] xl:min-h-[852px]',
  markdown: 'min-h-[1600px] md:min-h-[1530px] lg:min-h-[900px] xl:min-h-[880px]',
  'markdown-table-generator':
    'min-h-[854px] sm:min-h-[742px] md:min-h-[758px] lg:min-h-[432px] xl:min-h-[399px] 2xl:min-h-[396px]',
  'http-headers-inspector': 'min-h-[1120px] lg:min-h-[640px]',
  'cpu-benchmark': 'min-h-[360px] lg:min-h-[230px]',
  'cron-parser': 'min-h-[1207px] lg:min-h-[558px]',
  'date-calculator': 'min-h-[992px] lg:min-h-[460px]',
  'dead-pixel-test': 'min-h-[566px] lg:min-h-[348px]',
  'disk-speed-test': 'min-h-[602px] lg:min-h-[258px]',
  'gamepad-tester': 'min-h-[300px] lg:min-h-[220px]',
  'gpu-benchmark': 'min-h-[561px] lg:min-h-[818px]',
  'keyboard-tester': 'min-h-[818px] lg:min-h-[728px]',
  'memory-benchmark': 'min-h-[632px] lg:min-h-[358px]',
  'time-zone-converter': 'min-h-[1016px] lg:min-h-[420px]',
  timestamp: 'min-h-[572px] lg:min-h-[440px]',
  'timezone-meeting-planner': 'min-h-[644px] lg:min-h-[464px]',
  'touchscreen-tester': 'min-h-[610px] lg:min-h-[582px]',
  'user-agent-parser': 'min-h-[615px] lg:min-h-[375px]',
  'what-is-my-ip': 'min-h-[1182px] lg:min-h-[698px]',
};

const workspaceHeightFor = (toolId: string) => TOOL_WORKSPACE_HEIGHTS[toolId] ?? '';

// Loading fallback component
function ToolLoadingFallback({ toolId }: { toolId: string }) {
  const reservedHeight = workspaceHeightFor(toolId) || 'min-h-[400px]';
  const loadingAlignment =
    toolId === 'mock-data' || toolId === 'markdown' || toolId === 'markdown-table-generator'
      ? 'items-start pt-8'
      : 'items-center';

  return (
    <div
      className={`${reservedHeight} flex justify-center ${loadingAlignment}`}
      role="status"
      aria-live="polite"
    >
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
        <p className="text-gray-600">Loading tool...</p>
      </div>
    </div>
  );
}

export function ToolPage({
  toolId,
  component: Component,
  pageTitle,
  pageDescription,
  landing,
  componentProps,
}: ToolPageProps) {
  const tool = TOOLS.find((t) => t.id === toolId);
  const recordedToolId = useRef<string | null>(null);

  useEffect(() => {
    if (!tool || recordedToolId.current === toolId) return;
    recordToolUse(toolId);
    recordedToolId.current = toolId;
  }, [tool, toolId]);

  // Check if tool exists
  if (!tool) {
    return <Navigate to="/404" replace />;
  }

  const title = pageTitle ?? tool.name;
  const description = pageDescription ?? tool.description;
  const relatedTools = TOOLS.filter(
    (candidate) => candidate.category === tool.category && candidate.id !== tool.id,
  ).slice(0, 4);
  const workspaceHeight = workspaceHeightFor(toolId);

  return (
    <div className="space-y-10 pb-12">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-2">{description}</p>
      </div>

      <div className={`mt-6 ${workspaceHeight}`}>
        <ToolErrorBoundary key={landing?.path ?? toolId} toolId={toolId}>
          <Suspense fallback={<ToolLoadingFallback toolId={toolId} />}>
            <Component {...componentProps} {...(landing?.defaultProps ?? {})} />
          </Suspense>
        </ToolErrorBoundary>
      </div>

      <ToolFeedback toolId={toolId} toolName={title} />

      <ToolGuide title={title} tool={tool} relatedTools={relatedTools} landing={landing} />
    </div>
  );
}
